import { MinioService } from '@/api/minio';
import { IoIosClose, IoMdEye } from 'react-icons/io';
import { downloadFile } from '@/utils/libs/fileUtil';
import {
  FaDownload,
  FaFileCode,
  FaFileCsv,
  FaFileImage,
  FaFileVideo,
  FaRegFileExcel,
  FaRegFilePdf,
  FaRegFilePowerpoint,
  FaRegFileWord,
} from 'react-icons/fa6';
import { FaRegFileAlt, FaRegFileArchive } from 'react-icons/fa';
import { Loading } from '@/components/Loading';
import { useEffect, useState } from 'react';

interface Media {
  id: string;
  name: string;
  path: string;
  size: string;
  created_at: string;
  updated_at: string;
}

interface MediaAttachment {
  name: string;
  preview_url: string;
  path_download: string;
}

export const CardAttachment: React.FC<{
  title: string;
  media?: Media[];
}> = ({ title, media }) => {
  const { mutateAsync: fetchMediaFile, mutate: downloadFileFromMinio } =
    MinioService.useDownloadFile();
  const [mediaAttachment, setMediaAttachment] = useState<MediaAttachment[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(media?.length));
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const [urlModalOpened, setUrlModalOpened] = useState<string>('');
  const [attachmentName, setAttachmentName] = useState<string>('');

  const downloadAction = (
    filename: string | undefined,
    path: string | undefined,
  ) => {
    downloadFileFromMinio('/download' + path, {
      onSuccess: data => {
        downloadFile(data, filename ?? '');
      },
      onError: () => {
        console.error('Failed to download file');
      },
    });
  };

  const getFileIcon = (fileName: string) => {
    if (fileName.endsWith('.pdf'))
      return <FaRegFilePdf size={50} color="red" />;
    if (fileName.endsWith('.doc') || fileName.endsWith('.docx'))
      return <FaRegFileWord size={50} color="blue" />;
    if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx'))
      return <FaRegFileExcel size={50} color="green" />;
    if (fileName.endsWith('.csv')) return <FaFileCsv size={50} color="green" />;
    if (
      fileName.endsWith('.txt') ||
      fileName.endsWith('.log') ||
      fileName.endsWith('.ipynb')
    )
      return <FaRegFileAlt size={50} color="orange" />;
    if (fileName.endsWith('.zip'))
      return <FaRegFileArchive size={50} color="orange" />;
    if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx'))
      return <FaRegFilePowerpoint size={50} color="orangered" />;
    if (
      fileName.endsWith('.gif') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.jpg')
    )
      return <FaFileImage size={50} color="purple" />;
    if (
      fileName.endsWith('.mp4') ||
      fileName.endsWith('.webm') ||
      fileName.endsWith('.mov')
    )
      return <FaFileVideo size={50} color="black" />;
    return <FaFileCode size={50} color="gray" />;
  };

  useEffect(() => {
    let isMounted = true;

    const fetchMedia = async () => {
      if (!media?.length) {
        setMediaAttachment([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      const attachments = await Promise.all(
        media.map(async dataMedia => {
          try {
            const data = await fetchMediaFile('/download' + dataMedia.path);
            const url = URL.createObjectURL(data);

            return {
              name: dataMedia.name,
              preview_url: url,
              path_download: dataMedia.path,
            };
          } catch {
            return null;
          }
        }),
      );

      if (isMounted) {
        setMediaAttachment(
          attachments.filter(
            (attachment): attachment is MediaAttachment => attachment !== null,
          ),
        );
        setIsLoading(false);
      }
    };

    void fetchMedia();

    return () => {
      isMounted = false;
    };
  }, [fetchMediaFile, media]);

  return (
    <div className="flex flex-col w-full gap-1 p-2 border rounded">
      <span className="font-semibold">{title}</span>
      <div className="flex gap-4 flex-wrap">
        {!isLoading ? (
          mediaAttachment?.map(attachment => (
            <div key={attachment.name} className="relative max-w-max">
              {(() => {
                const imageRegex = /\.(jpg|png|gif|webp)$/i;
                const videoRegex = /\.(mp4|webm|mov)$/i;

                if (imageRegex.exec(attachment.name)) {
                  return (
                    <img
                      src={attachment.preview_url}
                      className="w-28 h-28"
                      alt="picture-attachment"
                    />
                  );
                } else if (videoRegex.exec(attachment.name)) {
                  return (
                    <video className="w-28 h-28" controls>
                      <source src={attachment.preview_url} type="video/mp4" />
                      <track kind="captions" />
                    </video>
                  );
                } else {
                  return (
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getFileIcon(attachment.name)}
                    </div>
                  );
                }
              })()}
              <div className="float-end flex gap-2 items-center mt-1">
                {attachment.name.match(/\.(jpg|png|gif|webp)$/i) && (
                  <button
                    onClick={() => {
                      setIsOpenModal(true);
                      setUrlModalOpened(attachment.preview_url);
                      setAttachmentName(attachment.name);
                    }}
                  >
                    <IoMdEye className="text-2xl" />
                  </button>
                )}
                {attachment.name.match(/\.(mp4|webm|mov)$/i) && (
                  <button
                    onClick={() => {
                      setIsOpenModal(true);
                      setUrlModalOpened(attachment.preview_url);
                    }}
                  >
                    <IoMdEye className="text-2xl" />
                  </button>
                )}
                <button
                  onClick={() =>
                    downloadAction(attachment.name, attachment.path_download)
                  }
                >
                  <FaDownload className="text-2xl" />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div>
            <Loading />
          </div>
        )}
      </div>
      {isOpenModal ? (
        <div className="bg-black/40 overflow-x-hidden overflow-y-auto fixed inset-0 z-50 flex justify-center items-center">
          <div className="flex flex-col justify-center items-center relative max-w-7xl w-full max-h-screen p-4">
            <div className="relative w-full flex justify-center">
              {(() => {
                if (/\.(jpg|png|gif|webp)$/i.exec(attachmentName)) {
                  return (
                    <img
                      src={urlModalOpened ?? ''}
                      className="max-w-full max-h-screen object-contain"
                      alt="picture-attachment"
                    />
                  );
                } else if (/\.(mp4|webm|mov)$/i.exec(attachmentName)) {
                  return (
                    <video width="100%" height="100%" controls>
                      <source src={urlModalOpened ?? ''} type="video/mp4" />
                      <track kind="captions" />
                    </video>
                  );
                } else {
                  return (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {getFileIcon(attachmentName)}
                    </div>
                  );
                }
              })()}
              <button
                className="opacity-30 hover:opacity-100 transition-all delay-100 bg-white p-1 rounded-full text-2xl absolute top-0 right-0 m-2"
                onClick={() => setIsOpenModal(false)}
              >
                <IoIosClose />
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
